import { Component, ElementRef, inject, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl } from '@angular/forms';

import { TranslatePipe } from '@ngx-translate/core';
import { ApiService } from '../../Services/api-service';
import { ApiResponse } from '../../models/api-response';
import { ActivatedRoute } from '@angular/router';

function luhnValidator(control: AbstractControl) {
  const value = (control.value || '').replace(/\s+/g, '');
  if (!value) return null;
  let sum = 0;
  let shouldDouble = false;
  for (let i = value.length - 1; i >= 0; i--) {
    let digit = parseInt(value.charAt(i), 10);
    if (isNaN(digit)) return { invalidCardNumber: true };
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return (sum % 10 === 0) ? null : { invalidCardNumber: true };
}

function futureYearValidator(control: AbstractControl) {
  const year = parseInt(control.value, 10);
  if (!year) return null;
  const currentYear = new Date().getFullYear();
  if (year < currentYear) return { pastYear: true };
  if (year > currentYear + 15) return { farFutureYear: true };
  return null;
}

function expiryNotPastValidator(group: AbstractControl) {
  const formGroup = group as FormGroup;
  const monthCtrl = formGroup.get('expiryMonth');
  const yearCtrl = formGroup.get('expiryYear');
  if (!monthCtrl || !yearCtrl) return null;
  const month = parseInt(monthCtrl.value, 10);
  const year = parseInt(yearCtrl.value, 10);
  if (!month || !year) return null;
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  if (year < currentYear || (year === currentYear && month < currentMonth)) {
    return { expiryPast: true };
  }
  return null;
}

@Component({
  selector: 'app-payment-component',
  imports: [ReactiveFormsModule, TranslatePipe],
  templateUrl: './payment-component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './payment-component.scss'
})
export class PaymentComponent {
  form!: FormGroup;
  @ViewChild('cardNumberInput') cardNumberInput!: ElementRef<HTMLInputElement>;
  @ViewChild('expiryMonthInput') expiryMonthInput!: ElementRef<HTMLInputElement>;
  @ViewChild('expiryYearInput') expiryYearInput!: ElementRef<HTMLInputElement>;
  @ViewChild('submitBtn') submitBtn!: ElementRef<HTMLButtonElement>;

  private readonly apiService = inject(ApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  constructor() {
    this.orderId.set(this.route.snapshot.paramMap.get('orderId'));
    this.form = this.fb.group({
      email: ['', [Validators.required]],
      cardHolderName: ['', [Validators.required, Validators.maxLength(64)]],
      cardNumber: ['', [Validators.required, Validators.minLength(16), Validators.maxLength(16), luhnValidator]],
      expiryMonth: ['', [Validators.required, Validators.pattern(/^(0?[1-9]|1[0-2])$/)]],
      expiryYear: ['', [Validators.required, Validators.pattern(/^\d{4}$/), futureYearValidator]],
    }, { validators: expiryNotPastValidator });
  }

  readonly submitting = signal(false);
  readonly successMessage = signal('');
  readonly errorMessage = signal('');
  orderId = signal<string | null>(null);
  readonly SUBSCRIBE_ENDPOINT = 'api/v1/Payment/AddCreditCard';


  get f(): { [key: string]: AbstractControl } { return this.form.controls; }



  submit(): void {
    if (this.form.invalid || this.submitting()) return;
    this.submitting.set(true);
    this.successMessage.set('');
    this.errorMessage.set('');
    const payload = {
      cardHolderName: this.f['cardHolderName'].value,
      email: this.f['email'].value,
      cardNumber: (this.f['cardNumber'].value || '').replace(/\s+/g, ''),
      expiryMonth: this.f['expiryMonth'].value,
      expiryYear: this.f['expiryYear'].value,
      orderId: this.orderId(),
    };
    this.apiService.postData(this.SUBSCRIBE_ENDPOINT, payload).subscribe({
      next: (response: ApiResponse) => {
        if (response.isSuccess) {
          this.successMessage.set('Payment_Success');
          this.form.reset();
          const checkoutUrl = response.data.checkoutUrl;
          window.location.href = checkoutUrl;
        } else {
          const msg = Array.isArray(response.messages) && response.messages.length
            ? response.messages.join(' ')
            : 'Checkout failed. Please try again later.';
          this.errorMessage.set(msg);
        }
        this.submitting.set(false);
      },
      error: () => {
        this.errorMessage.set('Payment_Error');
        this.submitting.set(false);
      }
    });
  }

  onCardNumberInput(ev: Event): void {
    const value = (this.f['cardNumber'].value || '').toString().replace(/\D/g, '');
    if (value.length >= 16 && this.expiryMonthInput) {
      this.expiryMonthInput.nativeElement.focus();
    }
  }

  onExpiryMonthInput(ev: Event): void {
    const value = (this.f['expiryMonth'].value || '').toString().replace(/\D/g, '');
    if (value.length >= 2 && this.expiryYearInput) {
      this.expiryYearInput.nativeElement.focus();
    }
  }

  onExpiryYearInput(ev: Event): void {
    const value = (this.f['expiryYear'].value || '').toString().replace(/\D/g, '');
    if (value.length >= 4 && this.submitBtn) {
      this.submitBtn.nativeElement.focus();
    }
  }
}
